import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Brain,
  CalendarCheck,
  CheckCircle2,
  Clock3,
  ClipboardCheck,
  Gauge,
  HeartPulse,
  Leaf,
  ListChecks,
  LucideIcon,
  PenLine,
  Scale,
  ShieldCheck,
  Sparkles,
  Target,
  Wind,
  Send,
  X,
  Zap,
} from 'lucide-react';
import { authApi, diariesApi } from '../api';

interface Props {
  onClose: () => void;
  onOpenEnergy?: () => void;
  onComposeDiary?: (type: 'mood' | 'gratitude') => void;
  onStatsUpdated?: () => void;
  energyEp?: number | null;
  energyEpData?: any;
}

type MoodCategory = 'positive' | 'neutral' | 'negative' | 'very_negative';
type MindTab = 'overview' | 'emotion' | 'rational' | 'training' | 'records';
type TrainingAction = 'mood' | 'gratitude' | 'self-check';
type BiasTone = 'cool' | 'watch' | 'warn';

interface MoodEffect {
  category: MoodCategory;
  label: string;
}

const MOOD_EFFECTS: Record<string, MoodEffect> = {
  '😊': { category: 'positive', label: '开心' },
  '😄': { category: 'positive', label: '大笑' },
  '🥰': { category: 'positive', label: '幸福' },
  '😌': { category: 'neutral', label: '平静' },
  '😎': { category: 'positive', label: '自信' },
  '🤗': { category: 'positive', label: '温暖' },
  '🥳': { category: 'positive', label: '庆祝' },
  '😍': { category: 'positive', label: '喜爱' },
  '😢': { category: 'negative', label: '难过' },
  '😭': { category: 'very_negative', label: '大哭' },
  '😤': { category: 'negative', label: '生气' },
  '😡': { category: 'very_negative', label: '愤怒' },
  '😰': { category: 'negative', label: '焦虑' },
  '😱': { category: 'very_negative', label: '害怕' },
  '😔': { category: 'negative', label: '失落' },
  '😩': { category: 'negative', label: '疲惫' },
  '🤒': { category: 'negative', label: '不适' },
  '🤔': { category: 'neutral', label: '思考' },
  '😴': { category: 'neutral', label: '困倦' },
  '🙄': { category: 'neutral', label: '无语' },
};

const clamp = (value: number, low = 0, high = 100) => Math.max(low, Math.min(high, Math.round(value)));

const scoreLabel = (score: number) => {
  if (score >= 85) return '优秀';
  if (score >= 70) return '良好';
  if (score >= 55) return '稳定';
  if (score >= 40) return '需关注';
  return '需恢复';
};

const formatDate = (value?: string) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.slice(0, 16);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const currentStreak = (diaries: any[]) => {
  const days = Array.from(new Set(
    diaries
      .map(d => d.date ? new Date(d.date) : null)
      .filter((d): d is Date => d !== null && !Number.isNaN(d.getTime()))
      .map(d => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime())
  )).sort((a, b) => b - a);
  if (!days.length) return 0;
  let streak = 1;
  let cursor = days[0];
  for (const day of days.slice(1)) {
    const diff = Math.round((cursor - day) / 86400000);
    if (diff === 1) {
      streak += 1;
      cursor = day;
    } else {
      break;
    }
  }
  return streak;
};

const includesAny = (text: string, words: string[]) => words.some(word => text.includes(word));

const countMatches = (text: string, words: string[]) => words.reduce((sum, word) => {
  if (!word) return sum;
  const matches = text.match(new RegExp(word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'));
  return sum + (matches?.length || 0);
}, 0);

const COGNITIVE_BIAS_RULES = [
  {
    key: 'catastrophizing',
    label: '灾难化',
    desc: '把事情快速推向最坏结局',
    keywords: ['完了', '崩了', '崩', '没救了', '糟糕', '灾难', '毁了', '来不及了', '肯定失败'],
    evidence: '出现最坏结果预判表达',
    suggestion: '写下“最坏结果 / 最可能结果 / 我能控制的一步”。',
  },
  {
    key: 'mind_reading',
    label: '读心术',
    desc: '把他人的想法当成确定事实',
    keywords: ['他觉得', '她觉得', '他们觉得', '肯定觉得', '一定觉得', '是不是讨厌', '看不起我', '不喜欢我'],
    evidence: '出现对他人想法的确定性推断',
    suggestion: '把“我猜他在想”改写成“我观察到的事实是”。',
  },
  {
    key: 'all_or_nothing',
    label: '非黑即白',
    desc: '用绝对词压缩复杂情况',
    keywords: ['总是', '从来', '完全', '一定', '必须', '永远', '一点都不', '全都', '根本'],
    evidence: '出现绝对化判断词',
    suggestion: '给判断加一个比例：这件事有几成是真的？反例是什么？',
  },
  {
    key: 'overgeneralization',
    label: '过度概括',
    desc: '用一次事件推导长期结论',
    keywords: ['每次都', '一直都', '我就是', '我永远', '我注定', '都这样', '又失败了'],
    evidence: '出现由单次事件外推自我结论的表达',
    suggestion: '把长期标签改成具体事件：这一次发生了什么？',
  },
  {
    key: 'self_blame',
    label: '过度归因',
    desc: '把复杂问题压到单一责任上',
    keywords: ['都怪我', '都是我的错', '都是因为', '全是我', '责任都在', '我不该'],
    evidence: '出现单一责任归因表达',
    suggestion: '拆成三栏：我的部分、他人的部分、环境的部分。',
  },
];

const MindCenter: React.FC<Props> = ({ onClose, onOpenEnergy, onComposeDiary, onStatsUpdated, energyEp, energyEpData }) => {
  const [loading, setLoading] = useState(true);
  const [san, setSan] = useState<any>(null);
  const [innerDiaries, setInnerDiaries] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<MindTab>('overview');
  const [selfCheckOpen, setSelfCheckOpen] = useState(false);
  const [selfCheck, setSelfCheck] = useState({ fact: '', interpretation: '', action: '' });
  const [savingSelfCheck, setSavingSelfCheck] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [completedTraining, setCompletedTraining] = useState<Record<string, boolean>>({});
  const [expandedRecordId, setExpandedRecordId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [sanRes, diaryRes] = await Promise.all([
        authApi.getSanStats(),
        diariesApi.getAll(),
      ]);
      setSan(sanRes.data);
      const all = ((diaryRes.data || []) as any[])
        .filter(d => ['mood', 'gratitude', 'grudge'].includes(d.diary_type))
        .sort((a, b) => new Date(b.date || b.created_at || 0).getTime() - new Date(a.date || a.created_at || 0).getTime());
      setInnerDiaries(all);
    } catch (e) {
      console.error('加载心维度失败', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const model = useMemo(() => {
    const sanValue = clamp(san?.san_value ?? 0);
    const typeCounts = san?.type_counts || {};
    const totalDiaries = san?.total_diaries ?? innerDiaries.length;
    const moodCounts = san?.mood_counts || {};
    const totalMood = Object.values(moodCounts).reduce((sum: number, n) => sum + Number(n || 0), 0) || 1;
    let positiveCount = 0;
    let neutralCount = 0;
    let negativeCount = 0;

    Object.entries(moodCounts).forEach(([emoji, raw]) => {
      const count = Number(raw || 0);
      const category = MOOD_EFFECTS[emoji]?.category || 'neutral';
      if (category === 'positive') positiveCount += count;
      else if (category === 'negative' || category === 'very_negative') negativeCount += count;
      else neutralCount += count;
    });

    const positiveRatio = Math.round((positiveCount / totalMood) * 100);
    const negativeRatio = Math.round((negativeCount / totalMood) * 100);
    const neutralRatio = Math.round((neutralCount / totalMood) * 100);
    const streakDays = san?.diary_streak_days ?? currentStreak(innerDiaries);
    const gratitudeCount = typeCounts.gratitude ?? innerDiaries.filter(d => d.diary_type === 'gratitude').length;
    const moodCount = typeCounts.mood ?? innerDiaries.filter(d => d.diary_type === 'mood').length;
    const grudgeCount = typeCounts.grudge ?? innerDiaries.filter(d => d.diary_type === 'grudge').length;
    const enhancedFallback = clamp(sanValue * 0.8 + Math.min(8, gratitudeCount * 2) + Math.min(6, moodCount) + Math.min(6, totalDiaries / 5));
    const enhanced = clamp(san?.enhanced_san ?? san?.san ?? enhancedFallback);

    const emotionStability = clamp(san?.emotion_stability ?? (sanValue - negativeRatio * 0.35 + positiveRatio * 0.12 + neutralRatio * 0.08));
    const emotionalHealth = clamp(sanValue * 0.46 + positiveRatio * 0.28 + emotionStability * 0.26);
    const rationalClarity = clamp(sanValue * 0.58 + Math.min(24, totalDiaries * 2.4) + Math.min(12, streakDays * 2));
    const recoveryScore = clamp(sanValue * 0.42 + Math.min(30, gratitudeCount * 7) + Math.min(18, positiveCount * 2.2) + Math.min(10, streakDays));
    const selfAwareness = clamp(Math.min(55, moodCount * 6 + grudgeCount * 4) + Math.min(25, streakDays * 4) + Math.min(20, totalDiaries));
    const mindResilience = clamp(enhanced * 0.42 + emotionalHealth * 0.18 + rationalClarity * 0.16 + recoveryScore * 0.12 + selfAwareness * 0.12);
    const mindEp = clamp(san?.mind_ep ?? mindResilience);

    const recent = innerDiaries.slice(0, 10);
    const latest = recent[0];
    const recentText = recent.map(d => `${d.content || ''}`).join(' ');
    const currentMoodIcon = latest?.mood || '';
    const currentMood = currentMoodIcon ? (MOOD_EFFECTS[currentMoodIcon]?.label || '已记录') : '未记录';
    const pressureLevel = clamp((100 - sanValue) / 13 + negativeRatio * 0.05 + grudgeCount * 0.2, 1, 10);
    let recommendedAction = '保持当前节奏';
    let actionDetail = '继续用短记录维护觉察，稳定比用力更重要。';
    if (sanValue < 45 || pressureLevel >= 7) {
      recommendedAction = '先恢复，再判断';
      actionDetail = '做一次情绪命名或 5 分钟呼吸放松，等内心降噪后再处理问题。';
    } else if (negativeRatio >= 35) {
      recommendedAction = '识别触发因素';
      actionDetail = '把事实、解释和感受分开写，避免直接进入结论。';
    } else if (rationalClarity >= 75) {
      recommendedAction = '记录一次理智自检';
      actionDetail = '你现在适合复盘一个小决策，把下次规则沉淀下来。';
    }

    const moodRows = Object.entries(moodCounts)
      .sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0))
      .slice(0, 8)
      .map(([emoji, count]) => ({
        emoji,
        count: Number(count || 0),
        label: MOOD_EFFECTS[emoji]?.label || '情绪',
        pct: Math.round((Number(count || 0) / totalMood) * 100),
      }));

    const triggerFactors = [
      { label: '计划压力', active: includesAny(recentText, ['计划', '任务', '进度', 'ddl', '截止', '来不及', '拖延']) },
      { label: '关系扰动', active: includesAny(recentText, ['朋友', '家人', '同事', '沟通', '关系', '误会', '争执']) },
      { label: '身体能量', active: includesAny(recentText, ['累', '困', '睡', '身体', '不舒服', '疲惫', '熬夜']) },
      { label: '自我评价', active: includesAny(recentText, ['不行', '失败', '焦虑', '担心', '害怕', '后悔', '内耗']) },
    ];

    const selfCheckEntries = recent.filter(d => String(d.content || '').includes('【理智自检】'));
    const factText = selfCheckEntries.map(d => String(d.content || '').match(/事实：([\s\S]*?)(解释：|行动：|$)/)?.[1] || '').join(' ');
    const interpretationText = selfCheckEntries.map(d => String(d.content || '').match(/解释：([\s\S]*?)(行动：|$)/)?.[1] || '').join(' ');
    const actionText = selfCheckEntries.map(d => String(d.content || '').match(/行动：([\s\S]*?)$/)?.[1] || '').join(' ');
    const structureSignals = [
      {
        key: 'inference_overload',
        label: '推论过载',
        active: interpretationText.trim().length > factText.trim().length * 1.6 && interpretationText.trim().length > 18,
        evidence: '自检中“解释”明显多于“事实”',
        suggestion: '下一次先写三条可验证事实，再写解释。',
      },
      {
        key: 'rumination',
        label: '反刍风险',
        active: selfCheckEntries.length > 0 && actionText.trim().length < 8,
        evidence: '自检中行动项较少或为空',
        suggestion: '把行动压小到 10 分钟内可完成的一步。',
      },
    ];
    const biasScan = COGNITIVE_BIAS_RULES.map(rule => {
      const keywordHits = countMatches(recentText, rule.keywords);
      const pressureBonus = rule.key === 'catastrophizing' && pressureLevel >= 7 ? 2 : 0;
      const grudgeBonus = rule.key === 'self_blame' && grudgeCount > 0 ? 1 : 0;
      const structureBonus = rule.key === 'all_or_nothing' && structureSignals[0].active ? 1 : 0;
      const score = keywordHits * 18 + pressureBonus * 12 + grudgeBonus * 10 + structureBonus * 8;
      const confidence = clamp(score, 0, 92);
      const level = confidence >= 55 ? '高' : confidence >= 26 ? '中' : '低';
      const tone: BiasTone = confidence >= 55 ? 'warn' : confidence >= 26 ? 'watch' : 'cool';
      const evidence = keywordHits > 0 ? `${rule.evidence} · ${keywordHits} 次` : '暂无明显语言证据';
      return { ...rule, keywordHits, confidence, level, tone, evidence };
    });
    const structureBias = structureSignals
      .filter(signal => signal.active)
      .map(signal => ({
        key: signal.key,
        label: signal.label,
        desc: signal.key === 'inference_overload' ? '解释多于事实时，判断容易被情绪带偏' : '只有想法没有行动时，容易停在内耗循环',
        keywordHits: 1,
        confidence: 64,
        level: '中',
        tone: 'watch' as BiasTone,
        evidence: signal.evidence,
        suggestion: signal.suggestion,
      }));
    const biasScanRows = [...biasScan, ...structureBias].sort((a, b) => b.confidence - a.confidence).slice(0, 5);
    const activeBiasCount = biasScanRows.filter(row => row.confidence >= 26).length;
    const biasSummary = activeBiasCount > 0
      ? `发现 ${activeBiasCount} 个值得自检的思维倾向`
      : '近期没有明显偏差警报，继续保持观察';

    const trainingItems = [
      { key: 'name-emotion', title: '3 分钟情绪命名', desc: '把感受命名，不急着解决', minutes: 3, exp: 10, done: Boolean(latest?.mood), color: '#fb7185', Icon: HeartPulse, action: 'mood' },
      { key: 'fact-interpretation', title: '事实/解释分离', desc: '客观事实与主观解释分开写', minutes: 5, exp: 15, done: rationalClarity >= 70, color: '#60a5fa', Icon: Scale, action: 'self-check' },
      { key: 'recovery-practice', title: '恢复练习', desc: '感恩、呼吸或短暂离场', minutes: 5, exp: 12, done: recoveryScore >= 70, color: '#4ade80', Icon: Leaf, action: 'gratitude' },
      { key: 'decision-review', title: '决策小复盘', desc: '沉淀一条下次可用规则', minutes: 7, exp: 18, done: false, color: '#fbbf24', Icon: ClipboardCheck, action: 'self-check' },
    ] as Array<{ key: string; title: string; desc: string; minutes: number; exp: number; done: boolean; color: string; Icon: LucideIcon; action: TrainingAction }>;

    const recoveryPlan = [
      sanValue < 60 ? '先降低输入量：暂停争论、新闻和高刺激内容 20 分钟。' : '保持当前节奏：今天适合做一次轻量复盘。',
      negativeRatio >= 35 ? '把负面事件写成「事实 / 我以为 / 我能做」三行。' : '继续用情绪命名维护稳定性。',
      pressureLevel >= 6 ? '安排一次 5 分钟呼吸或散步，让判断恢复到可用区间。' : '可以推进一个小决策，把下一步行动具体化。',
    ];

    return {
      sanValue,
      mindEp,
      mindResilience,
      statusLabel: san?.mind_ep_label || san?.san_status || '内心稳定',
      statusIcon: san?.san_status_icon || '🧠',
      statusShort: scoreLabel(mindEp),
      streakDays,
      positiveRatio,
      negativeRatio,
      currentMood,
      currentMoodIcon: currentMoodIcon || '·',
      pressureLevel,
      pressureText: pressureLevel <= 3 ? '低' : pressureLevel <= 6 ? '中等' : '偏高',
      dominantThought: latest?.content || '今天还没有记录主导想法',
      recommendedAction,
      actionDetail,
      moodRows,
      recent,
      triggerFactors,
      biasScanRows,
      biasSummary,
      trainingItems,
      recoveryPlan,
      rationalSteps: [
        { title: '事实', desc: '客观发生了什么？', detail: '可观察、可验证、不带评价', color: '#60a5fa' },
        { title: '解释', desc: '我如何理解它？', detail: '推论、假设、自动念头', color: '#c084fc' },
        { title: '行动', desc: '我将怎么做？', detail: '最小可行步、衡量标准', color: '#fbbf24' },
      ],
      capabilities: [
        { title: '情绪健康', score: emotionalHealth, label: scoreLabel(emotionalHealth), desc: emotionalHealth >= 70 ? '情绪稳定，波动可控' : '记录波动，优先恢复', color: '#fb7185', Icon: HeartPulse },
        { title: '理智清晰', score: rationalClarity, label: scoreLabel(rationalClarity), desc: rationalClarity >= 70 ? '思维清晰，判断稳定' : '先写事实，再下判断', color: '#60a5fa', Icon: Brain },
        { title: '压力恢复', score: recoveryScore, label: scoreLabel(recoveryScore), desc: recoveryScore >= 70 ? '恢复能力良好' : '增加感恩与放松练习', color: '#4ade80', Icon: Leaf },
        { title: '自我觉察', score: selfAwareness, label: scoreLabel(selfAwareness), desc: selfAwareness >= 70 ? '觉察深入，行动力提升' : '多记录触发因素', color: '#fbbf24', Icon: Target },
      ] as Array<{ title: string; score: number; label: string; desc: string; color: string; Icon: LucideIcon }>,
    };
  }, [san, innerDiaries]);

  const switchTab = (tab: MindTab) => {
    setActiveTab(tab);
    window.requestAnimationFrame(() => {
      document.getElementById('mind-tab-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const showFeedback = useCallback((message: string) => {
    setFeedback(message);
    window.setTimeout(() => setFeedback(''), 2400);
  }, []);

  const openSelfCheck = () => {
    setSelfCheckOpen(true);
    setActiveTab('rational');
  };

  const handleTrainingAction = (item: { key: string; action: TrainingAction; title: string }) => {
    if (item.action === 'self-check') {
      openSelfCheck();
      return;
    }
    if (item.action === 'mood') {
      onComposeDiary?.('mood');
      showFeedback('已进入情绪记录');
      return;
    }
    onComposeDiary?.('gratitude');
    showFeedback('已进入恢复练习');
  };

  const saveSelfCheck = async () => {
    const fact = selfCheck.fact.trim();
    const interpretation = selfCheck.interpretation.trim();
    const action = selfCheck.action.trim();
    if (!fact && !interpretation && !action) {
      showFeedback('先写下一点内容再保存');
      return;
    }

    try {
      setSavingSelfCheck(true);
      const content = [
        '【理智自检】',
        fact ? `事实：${fact}` : '',
        interpretation ? `解释：${interpretation}` : '',
        action ? `行动：${action}` : '',
      ].filter(Boolean).join('\n');

      await diariesApi.create({
        content,
        diary_type: 'mood',
        mood: '🤔',
        date: new Date().toISOString(),
      });
      setCompletedTraining(prev => ({
        ...prev,
        'fact-interpretation': true,
        'decision-review': true,
      }));
      setSelfCheck({ fact: '', interpretation: '', action: '' });
      setSelfCheckOpen(false);
      await load();
      onStatsUpdated?.();
      showFeedback('理智自检已保存');
    } catch (e) {
      console.error('保存理智自检失败', e);
      showFeedback('保存失败，请稍后再试');
    } finally {
      setSavingSelfCheck(false);
    }
  };

  const energyEpValue = typeof energyEp === 'number' ? energyEp : null;
  const energyBaseEp = energyEpData?.base_ep;
  const energyStreakBonus = energyEpData?.streak_bonus;

  return (
    <div className="mind-root">
      <header className="mind-header-bar">
        <button type="button" className="mind-icon-button" onClick={onClose} aria-label="返回">
          <ArrowLeft size={22} />
        </button>
        <h1>心 · 内心管理</h1>
        <button type="button" className="mind-trend-button" onClick={() => switchTab('emotion')}>
          <BarChart3 size={16} />
          <span>趋势</span>
        </button>
      </header>

      <main className="mind-main">
        {loading ? (
          <div className="mind-loading"><div className="mind-spinner" /><p>加载中...</p></div>
        ) : (
          <>
            <section className="mind-web-overview" aria-label="内心管理总览">
              <div className="mind-web-primary">
                <section className="mind-hero" aria-label="EP状态总览">
                  <div className="mind-ring" style={{ '--score': model.mindEp } as React.CSSProperties}>
                    <div className="mind-ring-copy">
                      <span>EP</span>
                      <strong>{model.mindEp}</strong>
                      <em>{model.statusShort}</em>
                    </div>
                  </div>
                  <div className="mind-san-panel">
                    <div className="mind-san-head">
                      <span>SAN 底层值</span>
                      <small>{model.statusIcon} {model.statusLabel}</small>
                    </div>
                    <div className="mind-san-value">{model.sanValue}<span>/100</span></div>
                    <div className="mind-track"><div className="mind-fill violet" style={{ width: `${model.sanValue}%` }} /></div>
                    <div className="mind-state"><span />内心状态可观察</div>
                    <p>EP综合 SAN、情绪健康、理智清晰和恢复能力，用一个值直观呈现当前内心状态。</p>
                  </div>
                </section>

                <section className="mind-status-strip" aria-label="关键状态">
                  <div><Brain size={15} /><span>清晰度</span><strong>{model.capabilities[1].score}</strong></div>
                  <div><Zap size={15} /><span>情绪波动</span><strong>{model.negativeRatio}%</strong></div>
                  <div><ShieldCheck size={15} /><span>行动一致性</span><strong>{model.streakDays}天</strong></div>
                </section>

                <section className="mind-capability-grid" aria-label="能力构成">
                  {model.capabilities.map(({ title, score, label, desc, color, Icon }) => (
                    <article className="mind-capability" key={title} style={{ '--tone': color } as React.CSSProperties}>
                      <div className="mind-capability-head">
                        <span><Icon size={18} /></span>
                        <h2>{title}</h2>
                      </div>
                      <div className="mind-capability-score">{score}<small>/100</small></div>
                      <div className="mind-track"><div className="mind-fill" style={{ width: `${score}%`, background: color }} /></div>
                      <p>{label} · {desc}</p>
                    </article>
                  ))}
                </section>
              </div>

              <aside className="mind-web-side">
                <section className="mind-section" id="mind-check">
                  <div className="mind-section-head">
                    <h2><CalendarCheck size={18} />今日内心检查</h2>
                    <button type="button" onClick={() => switchTab('records')}>查看历史 ›</button>
                  </div>
                  <div className="mind-check-list">
                    <div><span>当前情绪</span><strong>{model.currentMood}</strong><em>{model.currentMoodIcon}</em></div>
                    <div><span>压力水平</span><strong>{model.pressureText}</strong><em>{model.pressureLevel}/10</em></div>
                    <div><span>主导想法</span><strong>{model.dominantThought}</strong><em /></div>
                    <div><span>推荐下一步</span><strong>{model.recommendedAction}</strong><em>去练习</em></div>
                  </div>
                </section>

                <section className="mind-insight">
                  <div className="mind-insight-icon"><Sparkles size={22} /></div>
                  <div>
                    <small>今日洞察</small>
                    <strong>{model.recommendedAction}</strong>
                    <p>{model.actionDetail}</p>
                  </div>
                </section>

                <section className="mind-energy-card">
                  <div className="mind-energy-head">
                    <span><Zap size={17} />精力值</span>
                    <strong>{energyEpValue ?? '--'}<small>/100</small></strong>
                  </div>
                  <div className="mind-track"><div className="mind-fill energy" style={{ width: `${energyEpValue ?? 0}%` }} /></div>
                  <div className="mind-energy-meta">
                    <span>基础 {energyBaseEp ?? '--'}</span>
                    <span>连续 +{energyStreakBonus ?? 0}</span>
                  </div>
                  <p>精力值迁移到心模块内作为身体能量参考，EP仍是主页与本页的主状态值。</p>
                  {onOpenEnergy && <button type="button" onClick={onOpenEnergy}>查看精力详情</button>}
                </section>

                <div className="mind-actions compact" id="mind-actions">
                  <button type="button" className="mind-action pink" onClick={() => onComposeDiary?.('mood')}>
                    <PenLine size={20} />
                    <strong>情绪记录</strong>
                    <span>记录情绪波动</span>
                  </button>
                  <button type="button" className="mind-action blue" onClick={openSelfCheck}>
                    <Brain size={20} />
                    <strong>理智自检</strong>
                    <span>先写事实，再判断</span>
                  </button>
                </div>
              </aside>
            </section>

            <nav className="mind-tabs" aria-label="内心管理分区">
              <button type="button" className={activeTab === 'overview' ? 'active' : ''} onClick={() => switchTab('overview')}>总览</button>
              <button type="button" className={activeTab === 'emotion' ? 'active' : ''} onClick={() => switchTab('emotion')}>情绪</button>
              <button type="button" className={activeTab === 'rational' ? 'active' : ''} onClick={() => switchTab('rational')}>理智</button>
              <button type="button" className={activeTab === 'training' ? 'active' : ''} onClick={() => switchTab('training')}>训练</button>
              <button type="button" className={activeTab === 'records' ? 'active' : ''} onClick={() => switchTab('records')}>复盘</button>
            </nav>

            <section className="mind-tab-panel" id="mind-tab-panel">
              {activeTab === 'overview' && (
                <div className="mind-tab-grid">
                  <section className="mind-section mind-lab">
                    <div className="mind-section-head">
                      <h2><Scale size={18} />理智工作台</h2>
                      <span>事实优先</span>
                    </div>
                    <div className="mind-rational-grid">
                      {model.rationalSteps.map(step => (
                        <article key={step.title} style={{ '--step-color': step.color } as React.CSSProperties}>
                          <strong>{step.title}</strong>
                          <span>{step.desc}</span>
                          <p>{step.detail}</p>
                        </article>
                      ))}
                    </div>
                    <div className="mind-prompt-row">
                      <AlertTriangle size={16} />
                      <span>现在先写事实，不急着下结论。</span>
                      <button type="button" onClick={openSelfCheck}>开始记录</button>
                    </div>
                  </section>

                  <section className="mind-section">
                    <div className="mind-section-head">
                      <h2><Target size={18} />触发因素</h2>
                      <span>近期扫描</span>
                    </div>
                    <div className="mind-trigger-grid">
                      {model.triggerFactors.map(item => (
                        <span key={item.label} className={item.active ? 'active' : ''}>
                          {item.active ? <CheckCircle2 size={14} /> : <Clock3 size={14} />}
                          {item.label}
                        </span>
                      ))}
                    </div>
                  </section>

	                  <section className="mind-section">
	                    <div className="mind-section-head">
	                      <h2><Brain size={18} />认知偏差扫描</h2>
	                      <span>{model.biasSummary}</span>
	                    </div>
	                    <div className="mind-bias-cards compact">
	                      {model.biasScanRows.slice(0, 3).map(item => (
	                        <article key={item.key} className={item.tone}>
	                          <div>
	                            <strong>{item.label}</strong>
	                            <em>{item.level}</em>
	                          </div>
	                          <p>{item.desc}</p>
	                        </article>
	                      ))}
	                    </div>
	                  </section>

                  <section className="mind-section">
                    <div className="mind-section-head">
                      <h2><Wind size={18} />恢复计划</h2>
                      <span>下一步</span>
                    </div>
                    <ol className="mind-recovery-list">
                      {model.recoveryPlan.map(item => <li key={item}>{item}</li>)}
                    </ol>
                  </section>
                </div>
              )}

              {activeTab === 'emotion' && (
                <section className="mind-section mind-wide-panel" id="mind-emotion">
                  <div className="mind-section-head">
                    <h2><Gauge size={18} />情绪图谱</h2>
                    <span>正向 {model.positiveRatio}% · 负向 {model.negativeRatio}%</span>
                  </div>
                  {model.moodRows.length > 0 ? (
                    <>
                      <div className="mind-emotion-summary">
                        <div><span>正向</span><strong>{model.positiveRatio}%</strong></div>
                        <div><span>中性</span><strong>{Math.max(0, 100 - model.positiveRatio - model.negativeRatio)}%</strong></div>
                        <div><span>负向</span><strong>{model.negativeRatio}%</strong></div>
                      </div>
                      <div className="mind-mood-bars">
                        {model.moodRows.map(m => (
                          <div key={m.emoji}>
                            <span>{m.emoji} {m.label}</span>
                            <div className="mind-track"><div className="mind-fill" style={{ width: `${Math.max(7, m.pct)}%`, background: '#60a5fa' }} /></div>
                            <em>{m.count}次</em>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="mind-empty">
                      <HeartPulse size={24} />
                      <strong>还没有情绪样本</strong>
                      <p>记录一次情绪后，这里会生成情绪占比、波动与常见状态。</p>
                      <button type="button" onClick={() => onComposeDiary?.('mood')}>记录情绪</button>
                    </div>
                  )}
                </section>
              )}

              {activeTab === 'rational' && (
                <div className="mind-tab-grid">
                  <section className="mind-section mind-lab">
                    <div className="mind-section-head">
                      <h2><Scale size={18} />理智工作台</h2>
                      <span>事实 / 解释 / 行动</span>
                    </div>
                    <div className="mind-rational-grid">
                      {model.rationalSteps.map(step => (
                        <article key={step.title} style={{ '--step-color': step.color } as React.CSSProperties}>
                          <strong>{step.title}</strong>
                          <span>{step.desc}</span>
                          <p>{step.detail}</p>
                        </article>
                      ))}
                    </div>
                    <div className="mind-prompt-row">
                      <AlertTriangle size={16} />
                      <span>当情绪强度高时，只记录事实，不做最终判断。</span>
                      <button type="button" onClick={openSelfCheck}>新建自检</button>
                    </div>
                  </section>
	                  <section className="mind-section">
	                    <div className="mind-section-head">
	                      <h2><Brain size={18} />认知偏差扫描</h2>
	                      <span>{model.biasSummary}</span>
	                    </div>
	                    <div className="mind-bias-cards">
	                      {model.biasScanRows.map(item => (
	                        <article key={item.key} className={item.tone}>
	                          <div className="mind-bias-card-head">
	                            <div>
	                              <strong>{item.label}</strong>
	                              <small>{item.desc}</small>
	                            </div>
	                            <em>{item.level}</em>
	                          </div>
	                          <div className="mind-bias-meter">
	                            <span style={{ width: `${Math.max(8, item.confidence)}%` }} />
	                          </div>
	                          <p>{item.evidence}</p>
	                          <div className="mind-bias-action">
	                            <AlertTriangle size={14} />
	                            <span>{item.suggestion}</span>
	                          </div>
	                        </article>
	                      ))}
	                      {model.biasScanRows.length === 0 && (
	                        <div className="mind-empty">
	                          <Brain size={24} />
	                          <strong>暂无可扫描内容</strong>
	                          <p>完成几次情绪记录或理智自检后，这里会显示可解释的偏差提示。</p>
	                          <button type="button" onClick={openSelfCheck}>新建自检</button>
	                        </div>
	                      )}
	                    </div>
	                  </section>
                </div>
              )}

              {activeTab === 'training' && (
                <div className="mind-tab-grid">
                  <section className="mind-section">
                    <div className="mind-section-head">
                      <h2><ListChecks size={18} />今日内心训练</h2>
                      <span>{model.trainingItems.filter(i => i.done || completedTraining[i.key]).length}/{model.trainingItems.length} 项</span>
                    </div>
                    <div className="mind-training-list">
                      {model.trainingItems.map(item => {
                        const done = item.done || completedTraining[item.key];
                        return (
                          <article key={item.key} style={{ '--training-color': item.color } as React.CSSProperties}>
                            <span className="mind-training-icon"><item.Icon size={17} /></span>
                            <div>
                              <strong>{item.title}</strong>
                              <small>{item.desc}</small>
                            </div>
                            <em>{item.minutes}分钟 · +{item.exp}</em>
                            <button type="button" className="mind-training-button" onClick={() => handleTrainingAction(item)}>
                              {done ? '复看' : '开始'}
                            </button>
                            <span className={done ? 'mind-done on' : 'mind-done'}><CheckCircle2 size={16} /></span>
                          </article>
                        );
                      })}
                    </div>
                  </section>
                  <section className="mind-section">
                    <div className="mind-section-head">
                      <h2><Wind size={18} />恢复计划</h2>
                      <span>下一步</span>
                    </div>
                    <ol className="mind-recovery-list">
                      {model.recoveryPlan.map(item => <li key={item}>{item}</li>)}
                    </ol>
                  </section>
                  <div className="mind-actions">
                    <button type="button" className="mind-action green" onClick={() => onComposeDiary?.('gratitude')}>
                      <Leaf size={20} />
                      <strong>恢复练习</strong>
                      <span>调节压力与能量</span>
                    </button>
                    {onOpenEnergy && (
                      <button type="button" className="mind-action amber" onClick={onOpenEnergy}>
                        <Sparkles size={20} />
                        <strong>精力联动</strong>
                        <span>查看精力状态</span>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'records' && (
                <section className="mind-section mind-wide-panel" id="mind-records">
                  <div className="mind-section-head">
                    <h2><ClipboardCheck size={18} />近期内心记录</h2>
                    <span>连续 {model.streakDays} 天</span>
                  </div>
                  {model.recent.length > 0 ? (
                    <div className="mind-record-list">
                      {model.recent.map(d => {
                        const expanded = expandedRecordId === d.id;
                        return (
                          <button type="button" className={expanded ? 'mind-record expanded' : 'mind-record'} key={d.id} onClick={() => setExpandedRecordId(expanded ? null : d.id)}>
                            <div>
                              <small>{d.mood || ''} {d.diary_type} · {formatDate(d.date)}</small>
                              <p>{d.content || '无内容'}</p>
                            </div>
                            <span>{expanded ? '收起' : '展开'}</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="mind-empty">
                      <ClipboardCheck size={24} />
                      <strong>还没有内心记录</strong>
                      <p>完成情绪记录、感恩恢复或理智自检后，会在这里集中复盘。</p>
                      <button type="button" onClick={openSelfCheck}>新建自检</button>
                    </div>
                  )}
                </section>
              )}
            </section>
          </>
        )}
      </main>

      {feedback && <div className="mind-toast" role="status">{feedback}</div>}

      {selfCheckOpen && (
        <div className="mind-modal-backdrop" role="presentation" onClick={() => setSelfCheckOpen(false)}>
          <section className="mind-modal" role="dialog" aria-modal="true" aria-label="理智自检" onClick={e => e.stopPropagation()}>
            <div className="mind-modal-head">
              <div>
                <span>理智自检</span>
                <h2>事实 / 解释 / 行动</h2>
              </div>
              <button type="button" className="mind-icon-button" onClick={() => setSelfCheckOpen(false)} aria-label="关闭">
                <X size={20} />
              </button>
            </div>
            <label>
              <span>事实</span>
              <textarea value={selfCheck.fact} onChange={e => setSelfCheck(prev => ({ ...prev, fact: e.target.value }))} placeholder="客观发生了什么？只写可观察、可验证的部分。" />
            </label>
            <label>
              <span>解释</span>
              <textarea value={selfCheck.interpretation} onChange={e => setSelfCheck(prev => ({ ...prev, interpretation: e.target.value }))} placeholder="我脑中自动出现了什么解释？这里允许是假设。" />
            </label>
            <label>
              <span>行动</span>
              <textarea value={selfCheck.action} onChange={e => setSelfCheck(prev => ({ ...prev, action: e.target.value }))} placeholder="下一步最小行动是什么？怎样判断它完成了？" />
            </label>
            <div className="mind-modal-actions">
              <button type="button" onClick={() => setSelfCheckOpen(false)}>取消</button>
              <button type="button" className="primary" onClick={saveSelfCheck} disabled={savingSelfCheck}>
                <Send size={16} />
                {savingSelfCheck ? '保存中...' : '保存自检'}
              </button>
            </div>
          </section>
        </div>
      )}

      <style>{`
        .mind-root { min-height: 100vh; background: radial-gradient(circle at 50% -12%, rgba(79,70,229,.28), transparent 34%), radial-gradient(circle at 88% 10%, rgba(14,165,233,.12), transparent 32%), #030712; color: #f8fafc; padding-bottom: 56px; }
        .mind-header-bar { position: sticky; top: 0; z-index: 10; display: grid; grid-template-columns: 48px minmax(0, 1fr) 92px; align-items: center; gap: 12px; width: min(100%, 1280px); margin: 0 auto; box-sizing: border-box; padding: calc(14px + var(--status-bar-height, 0px)) 28px 14px; background: rgba(3,7,18,.88); backdrop-filter: blur(18px); border-bottom: 1px solid rgba(148,163,184,.1); }
        .mind-header-bar h1 { margin: 0; text-align: center; font-size: 20px; font-weight: 850; letter-spacing: 0; }
        .mind-icon-button, .mind-trend-button { border: 0; color: rgba(226,232,240,.92); background: transparent; cursor: pointer; }
        .mind-icon-button { width: 42px; height: 42px; display: grid; place-items: center; border-radius: 12px; }
        .mind-trend-button { display: inline-flex; align-items: center; justify-content: flex-end; gap: 5px; font-size: 13px; color: rgba(203,213,225,.74); }
        .mind-main { width: min(100%, 1280px); margin: 0 auto; box-sizing: border-box; padding: 22px 28px 34px; }
        .mind-loading { display: grid; place-items: center; min-height: 45vh; color: #94a3b8; }
        .mind-spinner { width: 34px; height: 34px; margin-bottom: 12px; border-radius: 999px; border: 3px solid rgba(148,163,184,.18); border-top-color: #a78bfa; animation: mindSpin .8s linear infinite; }
        .mind-web-overview { display: grid; grid-template-columns: minmax(0, 1.6fr) minmax(330px, .78fr); gap: 18px; align-items: start; margin-bottom: 18px; }
        .mind-web-primary { min-width: 0; }
        .mind-web-side { min-width: 0; display: flex; flex-direction: column; gap: 14px; position: sticky; top: 88px; }
        .mind-hero { display: grid; grid-template-columns: 190px minmax(0, 1fr); gap: 26px; align-items: center; min-height: 230px; padding: 28px; border: 1px solid rgba(148,163,184,.22); border-radius: 20px; background: radial-gradient(circle at 20% 12%, rgba(168,85,247,.22), transparent 36%), radial-gradient(circle at 90% 0%, rgba(96,165,250,.12), transparent 34%), linear-gradient(145deg, rgba(15,23,42,.94), rgba(17,24,39,.78)); box-shadow: 0 22px 52px rgba(2,6,23,.34); }
        .mind-ring { --score: 0; width: 190px; aspect-ratio: 1; display: grid; place-items: center; border-radius: 999px; background: radial-gradient(circle at center, rgba(15,23,42,.96) 0 58%, transparent 59%), conic-gradient(#c084fc calc(var(--score) * 1%), rgba(51,65,85,.82) 0); }
        .mind-ring-copy { text-align: center; }
        .mind-ring-copy span { display: block; color: rgba(203,213,225,.78); font-size: 12px; }
        .mind-ring-copy strong { display: block; margin: 7px 0; font-size: 64px; line-height: .95; font-weight: 920; }
        .mind-ring-copy em { display: inline-flex; padding: 3px 10px; border-radius: 999px; color: #e9d5ff; background: rgba(168,85,247,.18); font-size: 12px; font-style: normal; font-weight: 760; }
        .mind-san-panel { min-width: 0; padding-left: 18px; border-left: 1px solid rgba(148,163,184,.16); }
        .mind-san-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; color: rgba(203,213,225,.78); font-size: 13px; }
        .mind-san-head small { color: rgba(226,232,240,.82); font-size: 12px; white-space: nowrap; }
        .mind-san-value { margin: 12px 0 9px; color: #a78bfa; font-size: 56px; font-weight: 900; line-height: 1; }
        .mind-san-value span { color: rgba(148,163,184,.72); font-size: 16px; font-weight: 650; }
        .mind-track { height: 7px; overflow: hidden; border-radius: 999px; background: rgba(51,65,85,.82); }
        .mind-fill { height: 100%; border-radius: inherit; transition: width .35s ease; }
        .mind-fill.violet { background: linear-gradient(90deg, #8b5cf6, #d946ef); }
        .mind-state { display: inline-flex; align-items: center; gap: 7px; margin-top: 11px; font-size: 13px; font-weight: 740; color: rgba(226,232,240,.9); }
        .mind-state span { width: 8px; height: 8px; border-radius: 999px; background: #4ade80; box-shadow: 0 0 14px rgba(74,222,128,.55); }
        .mind-san-panel p { margin: 14px 0 0; max-width: 520px; color: rgba(203,213,225,.72); font-size: 14px; line-height: 1.6; }
        .mind-status-strip { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 1px; margin: 12px 0 0; overflow: hidden; border: 1px solid rgba(148,163,184,.14); border-radius: 14px; background: rgba(148,163,184,.1); }
        .mind-status-strip div { min-width: 0; display: grid; grid-template-columns: auto minmax(0,1fr); column-gap: 6px; row-gap: 3px; align-items: center; padding: 11px 10px; background: rgba(15,23,42,.78); }
        .mind-status-strip svg { color: #a78bfa; }
        .mind-status-strip span { min-width: 0; color: rgba(203,213,225,.68); font-size: 11px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .mind-status-strip strong { grid-column: 1 / -1; color: rgba(248,250,252,.94); font-size: 18px; line-height: 1; }
        .mind-tabs { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); max-width: 760px; margin: 4px 0 18px; overflow: hidden; border: 1px solid rgba(148,163,184,.18); border-radius: 15px; background: rgba(15,23,42,.58); }
        .mind-tabs button { padding: 13px 0 12px; border: 0; border-bottom: 2px solid transparent; color: rgba(203,213,225,.74); background: transparent; font: inherit; font-size: 14px; font-weight: 760; cursor: pointer; }
        .mind-tabs button.active { color: #e9d5ff; border-bottom-color: #c084fc; background: rgba(168,85,247,.11); }
        .mind-capability-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-top: 12px; }
        .mind-capability { min-width: 0; padding: 16px; border: 1px solid rgba(148,163,184,.16); border-radius: 15px; background: linear-gradient(180deg, rgba(30,41,59,.72), rgba(15,23,42,.64)); }
        .mind-capability-head { display: flex; align-items: center; gap: 9px; }
        .mind-capability-head span { width: 34px; height: 34px; display: grid; place-items: center; flex: 0 0 auto; border-radius: 999px; color: var(--tone); border: 1px solid color-mix(in srgb, var(--tone) 42%, transparent); background: color-mix(in srgb, var(--tone) 14%, transparent); }
        .mind-capability h2 { margin: 0; font-size: 15px; font-weight: 830; }
        .mind-capability-score { margin: 12px 0 6px; color: var(--tone); font-size: 31px; font-weight: 900; line-height: 1; }
        .mind-capability-score small { color: rgba(148,163,184,.72); font-size: 13px; font-weight: 650; }
        .mind-capability p { min-height: 36px; margin: 9px 0 0; color: rgba(203,213,225,.72); font-size: 12px; line-height: 1.45; }
        .mind-tab-panel { min-height: 330px; }
        .mind-tab-grid { display: grid; grid-template-columns: minmax(0, 1.25fr) minmax(300px, .75fr); gap: 16px; align-items: start; }
        .mind-wide-panel { max-width: 940px; }
        .mind-section { margin-bottom: 0; padding: 18px; border: 1px solid rgba(148,163,184,.16); border-radius: 17px; background: rgba(15,23,42,.62); }
        .mind-section-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
        .mind-section-head h2 { display: inline-flex; align-items: center; gap: 8px; margin: 0; font-size: 16px; font-weight: 850; }
        .mind-section-head button, .mind-section-head span { border: 0; background: transparent; color: rgba(203,213,225,.68); font-size: 12px; }
        .mind-check-list { overflow: hidden; border: 1px solid rgba(148,163,184,.13); border-radius: 13px; }
        .mind-check-list div { display: grid; grid-template-columns: 76px minmax(0,1fr) auto; gap: 10px; align-items: center; padding: 12px; border-bottom: 1px solid rgba(148,163,184,.1); }
        .mind-check-list div:last-child { border-bottom: 0; }
        .mind-check-list span { color: rgba(148,163,184,.86); font-size: 12px; }
        .mind-check-list strong { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: rgba(248,250,252,.94); font-size: 13px; }
        .mind-check-list em { color: #c084fc; font-size: 12px; font-style: normal; font-weight: 820; }
        .mind-lab { background: linear-gradient(180deg, rgba(15,23,42,.76), rgba(2,6,23,.7)); }
        .mind-rational-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); overflow: hidden; border: 1px solid rgba(148,163,184,.16); border-radius: 14px; }
        .mind-rational-grid article { min-width: 0; min-height: 132px; padding: 17px 14px; border-right: 1px solid rgba(148,163,184,.12); background: color-mix(in srgb, var(--step-color) 8%, rgba(15,23,42,.72)); }
        .mind-rational-grid article:last-child { border-right: 0; }
        .mind-rational-grid strong { display: block; color: var(--step-color); font-size: 16px; font-weight: 860; }
        .mind-rational-grid span { display: block; margin-top: 5px; color: rgba(248,250,252,.9); font-size: 12px; font-weight: 720; line-height: 1.35; }
        .mind-rational-grid p { margin: 8px 0 0; color: rgba(203,213,225,.66); font-size: 11px; line-height: 1.45; }
        .mind-prompt-row { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; gap: 9px; align-items: center; margin-top: 12px; padding: 11px; border: 1px solid rgba(96,165,250,.22); border-radius: 13px; background: rgba(37,99,235,.12); }
        .mind-prompt-row svg { color: #60a5fa; }
        .mind-prompt-row span { color: rgba(226,232,240,.88); font-size: 12px; }
        .mind-prompt-row button { border: 1px solid rgba(96,165,250,.34); border-radius: 10px; padding: 7px 10px; color: #bfdbfe; background: rgba(37,99,235,.18); font-size: 12px; font-weight: 760; }
        .mind-trigger-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
        .mind-trigger-grid span { display: inline-flex; align-items: center; gap: 6px; min-width: 0; padding: 9px 10px; border: 1px solid rgba(148,163,184,.14); border-radius: 12px; color: rgba(203,213,225,.72); background: rgba(30,41,59,.42); font-size: 12px; }
        .mind-trigger-grid span.active { color: #e9d5ff; border-color: rgba(192,132,252,.34); background: rgba(168,85,247,.13); }
        .mind-bias-cards { display: grid; grid-template-columns: 1fr; gap: 10px; }
        .mind-bias-cards.compact { grid-template-columns: 1fr; gap: 8px; }
        .mind-bias-cards article { min-width: 0; padding: 13px; border: 1px solid rgba(96,165,250,.2); border-radius: 14px; background: rgba(37,99,235,.1); }
        .mind-bias-cards article.watch { border-color: rgba(251,191,36,.28); background: rgba(251,191,36,.09); }
        .mind-bias-cards article.warn { border-color: rgba(251,113,133,.34); background: rgba(251,113,133,.1); }
        .mind-bias-cards.compact article { padding: 11px; }
        .mind-bias-cards.compact article > div { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
        .mind-bias-cards strong { display: block; color: rgba(248,250,252,.94); font-size: 13px; }
        .mind-bias-cards small { display: block; margin-top: 3px; color: rgba(203,213,225,.62); font-size: 11px; line-height: 1.35; }
        .mind-bias-cards em { display: inline-flex; align-items: center; justify-content: center; min-width: 30px; padding: 3px 7px; border-radius: 999px; color: #bfdbfe; background: rgba(255,255,255,.08); font-size: 11px; font-style: normal; font-weight: 780; }
        .mind-bias-cards article.watch em { color: #fde68a; }
        .mind-bias-cards article.warn em { color: #fecdd3; }
        .mind-bias-cards p { margin: 9px 0 0; color: rgba(203,213,225,.72); font-size: 12px; line-height: 1.45; }
        .mind-bias-card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
        .mind-bias-meter { height: 6px; margin-top: 11px; overflow: hidden; border-radius: 999px; background: rgba(51,65,85,.78); }
        .mind-bias-meter span { display: block; height: 100%; border-radius: inherit; background: linear-gradient(90deg, #60a5fa, #fbbf24); }
        .mind-bias-cards article.warn .mind-bias-meter span { background: linear-gradient(90deg, #fbbf24, #fb7185); }
        .mind-bias-action { display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 7px; align-items: start; margin-top: 10px; padding: 9px; border-radius: 11px; color: rgba(226,232,240,.86); background: rgba(15,23,42,.42); }
        .mind-bias-action svg { color: #fbbf24; }
        .mind-bias-action span { min-width: 0; font-size: 12px; line-height: 1.45; }
        .mind-insight { display: grid; grid-template-columns: 42px minmax(0, 1fr); gap: 12px; align-items: center; margin-bottom: 16px; padding: 15px; border: 1px solid rgba(168,85,247,.22); border-radius: 15px; background: linear-gradient(135deg, rgba(168,85,247,.15), rgba(15,23,42,.68)); }
        .mind-insight-icon { width: 42px; height: 42px; display: grid; place-items: center; border-radius: 999px; color: #e9d5ff; background: rgba(168,85,247,.26); }
        .mind-insight small { display: block; color: rgba(203,213,225,.65); font-size: 11px; }
        .mind-insight strong { display: block; margin: 3px 0 2px; color: #e9d5ff; font-size: 15px; }
        .mind-insight p { margin: 0; color: rgba(203,213,225,.78); font-size: 12px; line-height: 1.45; }
        .mind-energy-card { padding: 15px; border: 1px solid rgba(96,165,250,.2); border-radius: 15px; background: linear-gradient(135deg, rgba(37,99,235,.13), rgba(15,23,42,.68)); }
        .mind-energy-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 9px; }
        .mind-energy-head span { display: inline-flex; align-items: center; gap: 7px; color: rgba(191,219,254,.9); font-size: 13px; font-weight: 820; }
        .mind-energy-head strong { color: #93c5fd; font-size: 24px; line-height: 1; }
        .mind-energy-head small { color: rgba(203,213,225,.62); font-size: 12px; }
        .mind-fill.energy { background: linear-gradient(90deg, #2563eb, #7dd3fc); }
        .mind-energy-meta { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 8px; color: rgba(203,213,225,.68); font-size: 11px; }
        .mind-energy-card p { margin: 10px 0 0; color: rgba(203,213,225,.72); font-size: 12px; line-height: 1.45; }
        .mind-energy-card button { margin-top: 10px; width: 100%; border: 1px solid rgba(96,165,250,.28); border-radius: 11px; padding: 9px 10px; color: #bfdbfe; background: rgba(37,99,235,.14); font-size: 12px; font-weight: 760; cursor: pointer; }
        .mind-emotion-summary { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-bottom: 14px; }
        .mind-emotion-summary div { min-width: 0; padding: 13px; border: 1px solid rgba(148,163,184,.14); border-radius: 13px; background: rgba(30,41,59,.48); }
        .mind-emotion-summary span { display: block; color: rgba(203,213,225,.66); font-size: 12px; }
        .mind-emotion-summary strong { display: block; margin-top: 5px; color: rgba(248,250,252,.94); font-size: 24px; line-height: 1; }
        .mind-mood-bars { display: flex; flex-direction: column; gap: 10px; }
        .mind-mood-bars > div { display: grid; grid-template-columns: 96px minmax(0, 1fr) 44px; gap: 10px; align-items: center; }
        .mind-mood-bars span { color: rgba(226,232,240,.88); font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .mind-mood-bars em { color: rgba(203,213,225,.62); font-size: 11px; font-style: normal; text-align: right; }
        .mind-mood-chips { display: flex; flex-wrap: wrap; gap: 8px; }
        .mind-mood-chips span { display: inline-flex; padding: 9px 12px; border: 1px solid rgba(148,163,184,.14); border-radius: 999px; color: rgba(226,232,240,.88); background: rgba(30,41,59,.52); font-size: 13px; }
        .mind-record-list { display: flex; flex-direction: column; }
        .mind-record { width: 100%; display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 14px; padding: 14px 0; border: 0; border-bottom: 1px solid rgba(148,163,184,.1); color: inherit; background: transparent; text-align: left; cursor: pointer; }
        .mind-record:last-child { border-bottom: 0; }
        .mind-record small { display: block; margin-bottom: 4px; color: rgba(148,163,184,.78); font-size: 11px; }
        .mind-record p { margin: 0; color: rgba(226,232,240,.88); font-size: 13px; line-height: 1.45; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .mind-record.expanded p { display: block; white-space: pre-wrap; overflow: visible; }
        .mind-record > span { align-self: center; color: rgba(203,213,225,.48); font-size: 12px; white-space: nowrap; }
        .mind-training-list { display: flex; flex-direction: column; overflow: hidden; border: 1px solid rgba(148,163,184,.13); border-radius: 14px; }
        .mind-training-list article { display: grid; grid-template-columns: auto minmax(0,1fr) auto auto auto; gap: 12px; align-items: center; padding: 13px; border-bottom: 1px solid rgba(148,163,184,.1); }
        .mind-training-list article:last-child { border-bottom: 0; }
        .mind-training-icon { width: 34px; height: 34px; display: grid; place-items: center; border-radius: 999px; color: var(--training-color); background: color-mix(in srgb, var(--training-color) 14%, transparent); border: 1px solid color-mix(in srgb, var(--training-color) 36%, transparent); }
        .mind-training-list strong { display: block; color: rgba(248,250,252,.94); font-size: 13px; }
        .mind-training-list small { display: block; margin-top: 3px; color: rgba(203,213,225,.62); font-size: 11px; line-height: 1.35; }
        .mind-training-list em { color: rgba(203,213,225,.72); font-size: 11px; font-style: normal; white-space: nowrap; }
        .mind-training-button { border: 1px solid color-mix(in srgb, var(--training-color) 34%, transparent); border-radius: 10px; padding: 7px 10px; color: rgba(248,250,252,.9); background: color-mix(in srgb, var(--training-color) 13%, transparent); font-size: 12px; font-weight: 760; cursor: pointer; }
        .mind-done { width: 28px; height: 28px; display: grid; place-items: center; border-radius: 999px; color: rgba(148,163,184,.42); border: 1px solid rgba(148,163,184,.2); }
        .mind-done.on { color: #86efac; border-color: rgba(74,222,128,.36); background: rgba(74,222,128,.12); }
        .mind-recovery-list { margin: 0; padding-left: 20px; color: rgba(226,232,240,.84); font-size: 12px; line-height: 1.65; }
        .mind-recovery-list li + li { margin-top: 7px; }
        .mind-actions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
        .mind-actions.compact { margin-top: 0; }
        .mind-action { min-height: 78px; display: flex; flex-direction: column; justify-content: center; gap: 4px; padding: 12px; border-radius: 15px; border: 1px solid rgba(148,163,184,.16); color: rgba(248,250,252,.94); background: rgba(15,23,42,.72); text-align: left; cursor: pointer; }
        .mind-action.pink { border-color: rgba(251,113,133,.36); background: rgba(251,113,133,.12); }
        .mind-action.blue { border-color: rgba(96,165,250,.36); background: rgba(96,165,250,.12); }
        .mind-action.green { border-color: rgba(74,222,128,.32); background: rgba(74,222,128,.11); }
        .mind-action.amber { border-color: rgba(251,191,36,.32); background: rgba(251,191,36,.1); }
        .mind-action svg { color: currentColor; }
        .mind-action strong { font-size: 14px; }
        .mind-action span { color: rgba(203,213,225,.68); font-size: 11px; line-height: 1.35; }
        .mind-empty { min-height: 240px; display: grid; place-items: center; align-content: center; gap: 9px; padding: 24px; border: 1px dashed rgba(148,163,184,.24); border-radius: 15px; color: rgba(203,213,225,.72); text-align: center; background: rgba(15,23,42,.38); }
        .mind-empty svg { color: #a78bfa; }
        .mind-empty strong { color: rgba(248,250,252,.94); font-size: 15px; }
        .mind-empty p { max-width: 360px; margin: 0; font-size: 12px; line-height: 1.55; }
        .mind-empty button { margin-top: 4px; border: 1px solid rgba(168,85,247,.34); border-radius: 10px; padding: 8px 12px; color: #e9d5ff; background: rgba(168,85,247,.16); font-size: 12px; font-weight: 760; cursor: pointer; }
        .mind-toast { position: fixed; left: 50%; bottom: 28px; z-index: 40; transform: translateX(-50%); padding: 10px 14px; border: 1px solid rgba(148,163,184,.22); border-radius: 12px; color: rgba(248,250,252,.94); background: rgba(15,23,42,.94); box-shadow: 0 16px 40px rgba(0,0,0,.32); font-size: 13px; }
        .mind-modal-backdrop { position: fixed; inset: 0; z-index: 30; display: grid; place-items: center; padding: 24px; background: rgba(2,6,23,.68); backdrop-filter: blur(10px); }
        .mind-modal { width: min(100%, 620px); max-height: min(760px, calc(100vh - 48px)); overflow: auto; padding: 20px; border: 1px solid rgba(148,163,184,.2); border-radius: 18px; background: linear-gradient(180deg, rgba(15,23,42,.98), rgba(2,6,23,.96)); box-shadow: 0 28px 80px rgba(0,0,0,.46); }
        .mind-modal-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 14px; }
        .mind-modal-head span { display: block; color: rgba(203,213,225,.62); font-size: 12px; }
        .mind-modal-head h2 { margin: 4px 0 0; color: rgba(248,250,252,.96); font-size: 20px; line-height: 1.2; }
        .mind-modal label { display: block; margin-top: 12px; }
        .mind-modal label span { display: block; margin-bottom: 7px; color: rgba(226,232,240,.88); font-size: 13px; font-weight: 760; }
        .mind-modal textarea { width: 100%; min-height: 94px; box-sizing: border-box; resize: vertical; border: 1px solid rgba(148,163,184,.2); border-radius: 13px; padding: 12px; color: rgba(248,250,252,.94); background: rgba(15,23,42,.72); font: inherit; font-size: 13px; line-height: 1.5; outline: none; }
        .mind-modal textarea:focus { border-color: rgba(96,165,250,.5); box-shadow: 0 0 0 3px rgba(96,165,250,.12); }
        .mind-modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 16px; }
        .mind-modal-actions button { display: inline-flex; align-items: center; justify-content: center; gap: 7px; border: 1px solid rgba(148,163,184,.22); border-radius: 11px; padding: 10px 13px; color: rgba(226,232,240,.9); background: rgba(30,41,59,.58); font-size: 13px; font-weight: 760; cursor: pointer; }
        .mind-modal-actions button.primary { border-color: rgba(96,165,250,.44); color: #dbeafe; background: rgba(37,99,235,.22); }
        .mind-modal-actions button:disabled { opacity: .62; cursor: not-allowed; }
        @media (max-width: 980px) {
          .mind-header-bar, .mind-main { width: min(100%, 430px); padding-left: 16px; padding-right: 16px; }
          .mind-header-bar { grid-template-columns: 48px minmax(0, 1fr) 72px; padding-top: calc(14px + var(--status-bar-height, 28px)); }
          .mind-web-overview, .mind-tab-grid { grid-template-columns: 1fr; }
          .mind-web-side { position: static; }
          .mind-hero { grid-template-columns: 148px minmax(0, 1fr); gap: 18px; min-height: 0; padding: 20px 18px; }
          .mind-ring { width: 148px; }
          .mind-ring-copy strong { font-size: 52px; }
          .mind-san-value { font-size: 42px; }
          .mind-capability-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .mind-tabs { max-width: none; margin-top: 14px; }
        }
        @media (max-width: 430px) {
          .mind-hero { grid-template-columns: 1fr; }
          .mind-ring { margin: 0 auto; }
          .mind-san-panel { padding-left: 0; border-left: 0; }
          .mind-rational-grid { grid-template-columns: 1fr; }
          .mind-rational-grid article { border-right: 0; border-bottom: 1px solid rgba(148,163,184,.12); }
          .mind-rational-grid article:last-child { border-bottom: 0; }
          .mind-training-list article { grid-template-columns: auto minmax(0,1fr) auto; }
          .mind-training-list em { grid-column: 2 / 3; }
          .mind-training-button { grid-column: 2 / 3; width: fit-content; }
          .mind-done { grid-row: 1 / 4; grid-column: 3 / 4; }
          .mind-mood-bars > div { grid-template-columns: 86px minmax(0, 1fr) 38px; }
          .mind-emotion-summary { grid-template-columns: 1fr; }
          .mind-actions { grid-template-columns: 1fr; }
          .mind-modal-backdrop { padding: 12px; align-items: end; }
          .mind-modal { max-height: calc(100vh - 24px); border-radius: 17px 17px 0 0; }
        }
        @keyframes mindSpin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default MindCenter;
